import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const RoleAssignmentModal = ({ member, roles, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    roleId: '',
    department: member?.department || ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const activeRoles = roles?.filter(role => role?.isActive) || [];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!formData?.roleId) {
      setError('Please select a role');
      return;
    }

    if (!formData?.department) {
      setError('Please specify a department');
      return;
    }

    try {
      setLoading(true);
      await onSubmit?.({
        userEmail: member?.email,
        roleId: formData?.roleId,
        department: formData?.department
      });
    } catch (err) {
      setError(err?.message || 'Failed to assign role');
    } finally {
      setLoading(false);
    }
  };

  const selectedRole = activeRoles?.find(r => r?.id === formData?.roleId);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-bold text-foreground">Assign Role</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Member Info */}
          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-lg font-semibold text-primary">
                  {member?.firstName?.[0]}{member?.lastName?.[0]}
                </span>
              </div>
              <div>
                <p className="font-semibold text-foreground">{member?.fullName}</p>
                <p className="text-sm text-muted-foreground">{member?.email}</p>
                <p className="text-xs text-muted-foreground">{member?.department} • {member?.position}</p>
              </div>
            </div>

            {/* Current Roles */}
            {member?.roles?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs font-medium text-muted-foreground mb-2">Current Roles:</p>
                <div className="flex flex-wrap gap-1">
                  {member?.roles?.map((role) => (
                    <span
                      key={role?.assignmentId}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary"
                    >
                      <Icon name="Shield" size={10} />
                      {role?.roleName}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-4">
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Role Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Select Role <span className="text-error">*</span>
            </label>
            <select
              value={formData?.roleId}
              onChange={(e) => handleChange('roleId', e?.target?.value)}
              className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              required
            >
              <option value="">Choose a role...</option>
              {activeRoles?.map((role) => (
                <option key={role?.id} value={role?.id}>
                  {role?.roleName} {role?.isSystemRole ? '(System)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Role Description */}
          {selectedRole && (
            <div className="mb-4 p-3 bg-muted/50 rounded-md">
              <p className="text-sm text-muted-foreground">{selectedRole?.roleDescription}</p>
            </div>
          )}

          {/* Department */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Department Scope <span className="text-error">*</span>
            </label>
            <input
              type="text"
              value={formData?.department}
              onChange={(e) => handleChange('department', e?.target?.value)}
              placeholder="e.g., Human Resources, Finance"
              className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              Specify the department this role assignment applies to
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  <span className="ml-2">Assigning...</span>
                </>
              ) : (
                <>
                  <Icon name="UserPlus" size={16} />
                  <span className="ml-2">Assign Role</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleAssignmentModal;